import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import {
  getPublicUnits,
  getPublicCalendar,
  checkAvailability,
  submitPublicInquiry,
  type PublicUnit,
  type PublicCalendarEntry,
  type AvailabilityConflict,
} from "@/api/public";
import { ANREDEN, GRUPPENARTEN } from "@/api/constants";
import { Card } from "@/components/Card";
import { useDateRange } from "@/lib/useDateRange";
import { useDocumentTitle } from "@/lib/useDocumentTitle";

// ---------------------------------------------------------------------------
// Konstanten
// ---------------------------------------------------------------------------

const MONTHS_DE = [
  "Januar","Februar","März","April","Mai","Juni",
  "Juli","August","September","Oktober","November","Dezember",
];

const DOW_SHORT = ["S","M","D","M","D","F","S"];

const UNIT_COLOR_MAP: Record<string, string> = {
  "EG": "var(--unit-eg)",
  "OG": "var(--unit-og)",
  "Zeltplatz": "var(--unit-zp)",
  "DG": "var(--unit-dg)",
  "LZ": "var(--unit-lz)",
};

function unitColor(name: string): string {
  return UNIT_COLOR_MAP[name] ?? "var(--brand-700)";
}

// ---------------------------------------------------------------------------
// Hilfsfunktionen
// ---------------------------------------------------------------------------

function daysInMonth(year: number, month: number): number {
  return new Date(year, month + 1, 0).getDate();
}

function isoDate(year: number, month: number, day: number): string {
  return `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

function formatDe(iso: string): string {
  if (!iso || iso.length < 10) return iso;
  const [y, m, d] = iso.slice(0, 10).split("-");
  return `${d}.${m}.${y}`;
}

// ---------------------------------------------------------------------------
// Styles (inline, analog KonusFormPage)
// ---------------------------------------------------------------------------

const labelStyle: React.CSSProperties = {
  display: "block",
  fontSize: 11,
  fontWeight: 600,
  color: "var(--fg-muted)",
  marginBottom: 4,
  textTransform: "uppercase",
  letterSpacing: ".04em",
};

const inputStyle: React.CSSProperties = {
  width: "100%",
  padding: "8px 10px",
  borderRadius: 6,
  border: "1px solid var(--border)",
  background: "var(--bg-card)",
  color: "var(--fg)",
  fontSize: 13,
  fontFamily: "var(--font-body)",
  boxSizing: "border-box",
};

// ---------------------------------------------------------------------------
// Öffentlicher Kalender (datensparsam)
// ---------------------------------------------------------------------------

function PublicCalendarView({
  year, month, numDays, entries, unitNames, today,
}: {
  year: number;
  month: number;
  numDays: number;
  entries: PublicCalendarEntry[];
  unitNames: string[];
  today: Date;
}) {
  const days = Array.from({ length: numDays }, (_, i) => i + 1);
  const todayInView = today.getFullYear() === year && today.getMonth() === month;

  return (
    <div style={{ overflowX: "auto" }}>
      <div style={{ minWidth: 500 }}>
        {/* Header */}
        <div style={{
          display: "flex",
          borderBottom: "1px solid var(--border)",
          background: "var(--bg-subtle)",
          borderRadius: "8px 8px 0 0",
        }}>
          <div style={{
            width: 110, flexShrink: 0, padding: "8px 10px",
            fontSize: 10, textTransform: "uppercase", letterSpacing: ".06em",
            color: "var(--fg-muted)", fontWeight: 600,
            borderRight: "1px solid var(--border)",
          }}>Einheit</div>
          <div style={{ flex: 1, display: "flex" }}>
            {days.map(d => {
              const dow = new Date(year, month, d).getDay();
              const isWe = dow === 0 || dow === 6;
              const isToday = todayInView && d === today.getDate();
              return (
                <div key={d} style={{
                  flex: 1,
                  minWidth: 20,
                  textAlign: "center",
                  padding: "5px 0",
                  fontSize: 9,
                  fontWeight: isToday ? 700 : 500,
                  color: isToday ? "#fff" : (isWe ? "var(--accent)" : "var(--fg-muted)"),
                  background: isToday
                    ? "var(--brand-700)"
                    : (isWe ? "color-mix(in oklab, var(--accent) 6%, transparent)" : "transparent"),
                  borderRight: "1px solid var(--border)",
                  lineHeight: 1.2,
                }}>
                  <div style={{ textTransform: "uppercase", fontSize: 8, opacity: .7 }}>{DOW_SHORT[dow]}</div>
                  <div style={{ fontSize: 10, marginTop: 1 }}>{d}</div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Einheiten-Zeilen */}
        {unitNames.map(unitName => {
          const color = unitColor(unitName);
          const unitEntries = entries.filter(e => e.einheit_name === unitName);

          return (
            <div key={unitName} style={{
              display: "flex",
              borderBottom: "1px solid var(--border)",
              minHeight: 52,
              position: "relative",
            }}>
              <div style={{
                width: 110, flexShrink: 0, padding: "10px 10px",
                borderRight: "1px solid var(--border)",
                display: "flex", alignItems: "center", gap: 6,
              }}>
                <span style={{ width: 8, height: 8, borderRadius: 2, background: color, flexShrink: 0 }} />
                <span style={{ fontSize: 12, fontWeight: 600 }}>{unitName}</span>
              </div>

              <div style={{ flex: 1, position: "relative" }}>
                {/* Wochend-Hintergrund */}
                <div style={{ position: "absolute", inset: 0, display: "flex" }}>
                  {days.map(d => {
                    const dow = new Date(year, month, d).getDay();
                    const isWe = dow === 0 || dow === 6;
                    return (
                      <div key={d} style={{
                        flex: 1,
                        borderRight: "1px solid var(--border)",
                        background: isWe ? "color-mix(in oklab, var(--accent) 4%, transparent)" : "transparent",
                      }} />
                    );
                  })}
                </div>

                {/* Heute-Linie */}
                {todayInView && (
                  <div style={{
                    position: "absolute", top: 0, bottom: 0,
                    left: `${((today.getDate() - 0.5) / numDays) * 100}%`,
                    width: 2, background: "var(--brand-700)", zIndex: 2, pointerEvents: "none",
                  }} />
                )}

                {/* Belegungs-Balken (nur "belegt", keine Gastnamen) */}
                {unitEntries.map((e, idx) => {
                  const s = new Date(e.check_in);
                  const end = new Date(e.check_out);
                  const ms = new Date(year, month, 1);
                  const me = new Date(year, month, numDays);

                  const startDay = s < ms
                    ? 1
                    : (s.getFullYear() === year && s.getMonth() === month ? s.getDate() : 1);
                  const endDay = end > me
                    ? numDays
                    : (end.getFullYear() === year && end.getMonth() === month ? end.getDate() : numDays);

                  const leftPct = ((startDay - 1) / numDays) * 100;
                  const widthPct = ((endDay - startDay + 1) / numDays) * 100;

                  const checkInStr = e.check_in.length >= 10 ? formatDe(e.check_in) : e.check_in;
                  const checkOutStr = e.check_out.length >= 10 ? formatDe(e.check_out) : e.check_out;

                  return (
                    <div
                      key={idx}
                      title={`Belegt: ${checkInStr} – ${checkOutStr}`}
                      style={{
                        position: "absolute",
                        left: `calc(${leftPct}% + 2px)`,
                        width: `calc(${widthPct}% - 4px)`,
                        top: 8,
                        height: 36,
                        background: "var(--brand-700)",
                        opacity: 0.75,
                        borderRadius: 5,
                        padding: "4px 8px",
                        color: "#fff",
                        overflow: "hidden",
                        display: "flex",
                        alignItems: "center",
                        fontSize: 10,
                        fontWeight: 600,
                        zIndex: 1,
                      }}
                    >
                      Belegt
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}

        {/* Legende */}
        <div style={{
          padding: "8px 10px",
          display: "flex", gap: 16, fontSize: 11,
          color: "var(--fg-muted)", background: "var(--bg-subtle)",
          borderTop: "1px solid var(--border)", alignItems: "center",
          borderRadius: "0 0 8px 8px",
        }}>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <span style={{ width: 14, height: 10, background: "var(--brand-700)", opacity: .75, borderRadius: 3 }} />
            Belegt
          </span>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <span style={{ width: 14, height: 10, background: "transparent", borderRadius: 3 }} />
            Frei
          </span>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <span style={{ width: 2, height: 14, background: "var(--brand-700)" }} />
            Heute
          </span>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Haupt-Formular
// ---------------------------------------------------------------------------

type FormState = {
  gruppenname: string;
  anrede: string;
  vorname: string;
  nachname: string;
  email: string;
  telefon: string;
  gruppenart: string;
  adresse_strasse: string;
  adresse_plz: string;
  adresse_ort: string;
  adresse_land: string;
  check_in: string;
  check_out: string;
  check_in_time: string;
  check_out_time: string;
  selectedUnitIds: number[];
  nachricht: string;
  datenschutz_ok: boolean;
};

const EMPTY_FORM: FormState = {
  gruppenname: "",
  anrede: "",
  vorname: "",
  nachname: "",
  email: "",
  telefon: "",
  gruppenart: "",
  adresse_strasse: "",
  adresse_plz: "",
  adresse_ort: "",
  adresse_land: "Deutschland",
  check_in: "",
  check_out: "",
  check_in_time: "15:00",
  check_out_time: "10:00",
  selectedUnitIds: [],
  nachricht: "",
  datenschutz_ok: false,
};

export function PublicInquiryPage() {
  useDocumentTitle("Anfrage");
  const today = new Date();
  const [calMonth, setCalMonth] = useState(today.getMonth());
  const [calYear, setCalYear] = useState(today.getFullYear());

  const [units, setUnits] = useState<PublicUnit[]>([]);
  const [calEntries, setCalEntries] = useState<PublicCalendarEntry[]>([]);
  const [calLoading, setCalLoading] = useState(true);

  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [conflicts, setConflicts] = useState<AvailabilityConflict[]>([]);
  const [hasConflict, setHasConflict] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Einheiten laden
  useEffect(() => {
    getPublicUnits().then(setUnits).catch(() => {});
  }, []);

  // Kalender laden wenn Monat/Jahr wechselt
  useEffect(() => {
    setCalLoading(true);
    const numDays = daysInMonth(calYear, calMonth);
    getPublicCalendar(isoDate(calYear, calMonth, 1), isoDate(calYear, calMonth, numDays))
      .then(setCalEntries)
      .catch(() => {})
      .finally(() => setCalLoading(false));
  }, [calYear, calMonth]);

  // Einheiten nach Gruppenart filtern
  const availableUnits = useMemo(() => {
    const isBdP = form.gruppenart === "BdP BaWü (Stämme oder LV)";
    return units.filter(u => isBdP || u.name !== "DG");
  }, [units, form.gruppenart]);

  // Wenn Gruppenart wechselt und DG war ausgewählt: DG abwählen
  useEffect(() => {
    if (form.gruppenart !== "BdP BaWü (Stämme oder LV)") {
      const dgIds = units.filter(u => u.name === "DG").map(u => u.id);
      if (dgIds.some(id => form.selectedUnitIds.includes(id))) {
        setForm(f => ({
          ...f,
          selectedUnitIds: f.selectedUnitIds.filter(id => !dgIds.includes(id)),
        }));
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.gruppenart]);

  // Zeltplatz-mit-EG-Validierung
  const zeltplatzFehler = useMemo(() => {
    const zeltplatz = units.find(u => u.name === "Zeltplatz");
    if (!zeltplatz) return null;
    if (!form.selectedUnitIds.includes(zeltplatz.id)) return null;
    const requiredId = zeltplatz.nur_mit_einheit_id;
    if (!requiredId) return null;
    if (form.selectedUnitIds.includes(requiredId)) return null;
    const requiredUnit = units.find(u => u.id === requiredId);
    return `Der Zeltplatz kann nur zusammen mit ${requiredUnit?.name ?? "einer anderen Einheit"} gebucht werden.`;
  }, [units, form.selectedUnitIds]);

  // Konfliktprüfung (debounced)
  const checkConflicts = useCallback(() => {
    if (!form.check_in || !form.check_out || form.selectedUnitIds.length === 0) {
      setHasConflict(false);
      setConflicts([]);
      return;
    }
    checkAvailability({
      check_in: form.check_in,
      check_out: form.check_out,
      check_in_time: form.check_in_time,
      check_out_time: form.check_out_time,
      einheiten_ids: form.selectedUnitIds,
    }).then(result => {
      setHasConflict(result.has_conflict);
      setConflicts(result.conflicts);
    }).catch(() => {
      setHasConflict(false);
      setConflicts([]);
    });
  }, [form.check_in, form.check_out, form.check_in_time, form.check_out_time, form.selectedUnitIds]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(checkConflicts, 400);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [checkConflicts]);

  function set<K extends keyof FormState>(key: K) {
    return (val: FormState[K]) => setForm(f => ({ ...f, [key]: val }));
  }

  const dateRange = useDateRange(
    form.check_in,
    form.check_out,
    ({ from, to }) => setForm(f => ({ ...f, check_in: from, check_out: to })),
    { minNights: 1, keepDuration: true },
  );

  function toggleUnit(id: number) {
    setForm(f => ({
      ...f,
      selectedUnitIds: f.selectedUnitIds.includes(id)
        ? f.selectedUnitIds.filter(x => x !== id)
        : [...f.selectedUnitIds, id],
    }));
  }

  function validate(): string | null {
    if (!form.gruppenname.trim()) return "Gruppenname fehlt.";
    if (!form.anrede) return "Anrede fehlt.";
    if (!form.vorname.trim()) return "Vorname fehlt.";
    if (!form.nachname.trim()) return "Nachname fehlt.";
    if (!form.email.trim() || !form.email.includes("@")) return "Gültige E-Mail-Adresse erforderlich.";
    if (!form.gruppenart) return "Gruppenart fehlt.";
    if (!form.adresse_strasse.trim()) return "Straße fehlt.";
    if (!form.adresse_plz.trim()) return "PLZ fehlt.";
    if (!form.adresse_ort.trim()) return "Ort fehlt.";
    if (!form.check_in) return "Anreisedatum fehlt.";
    if (!form.check_out) return "Abreisedatum fehlt.";
    if (form.check_in >= form.check_out) return "Abreisedatum muss nach dem Anreisedatum liegen.";
    if (form.selectedUnitIds.length === 0) return "Bitte mindestens eine Einheit auswählen.";
    if (zeltplatzFehler) return zeltplatzFehler;
    if (!form.datenschutz_ok) return "Datenschutzeinwilligung erforderlich.";
    if (hasConflict) return "Für den gewählten Zeitraum liegt ein Belegungskonflikt vor.";
    return null;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitError(null);
    const err = validate();
    if (err) { setSubmitError(err); return; }

    const unitNames = form.selectedUnitIds
      .map(id => units.find(u => u.id === id)?.name ?? "")
      .filter(Boolean);

    setSubmitting(true);
    try {
      const result = await submitPublicInquiry({
        gruppenname: form.gruppenname.trim(),
        anrede: form.anrede,
        vorname: form.vorname.trim(),
        nachname: form.nachname.trim(),
        email: form.email.trim(),
        telefon: form.telefon.trim(),
        gruppenart: form.gruppenart,
        adresse_strasse: form.adresse_strasse.trim(),
        adresse_plz: form.adresse_plz.trim(),
        adresse_ort: form.adresse_ort.trim(),
        adresse_land: form.adresse_land.trim() || "Deutschland",
        check_in: form.check_in,
        check_out: form.check_out,
        check_in_time: form.check_in_time,
        check_out_time: form.check_out_time,
        einheiten_wunsch: unitNames,
        nachricht: form.nachricht.trim(),
        datenschutz_ok: form.datenschutz_ok,
        website: "",
        source: "website",
      });
      setSubmitSuccess(result.verification_required
        ? "Ihre Anfrage ist eingegangen! Bitte bestätigen Sie Ihre E-Mail-Adresse über den Link in der Bestätigungsmail."
        : "Ihre Anfrage ist eingegangen! Wir melden uns in Kürze bei Ihnen."
      );
      setForm(EMPTY_FORM);
      setHasConflict(false);
      setConflicts([]);
    } catch (err: unknown) {
      setSubmitError(err instanceof Error ? err.message : "Fehler beim Senden.");
    } finally {
      setSubmitting(false);
    }
  }

  const calNumDays = daysInMonth(calYear, calMonth);

  const allUnitNames = useMemo(() => {
    const fromUnits = units.map(u => u.name);
    const fromEntries = calEntries.map(e => e.einheit_name).filter(Boolean);
    return [...new Set([...fromUnits, ...fromEntries])];
  }, [units, calEntries]);

  const prevMonth = () => {
    if (calMonth === 0) { setCalMonth(11); setCalYear(y => y - 1); }
    else setCalMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (calMonth === 11) { setCalMonth(0); setCalYear(y => y + 1); }
    else setCalMonth(m => m + 1);
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  if (submitSuccess) {
    return (
      <div style={{
        minHeight: "100dvh", background: "var(--bg)", display: "flex",
        alignItems: "center", justifyContent: "center", padding: 24,
      }}>
        <Card style={{ maxWidth: 520, textAlign: "center", padding: 40 }}>
          <span className="material-icons-outlined" style={{ fontSize: 48, color: "var(--brand-700)" }}>check_circle</span>
          <h2 style={{ marginTop: 16, marginBottom: 8, fontSize: 22 }}>Anfrage eingegangen</h2>
          <p style={{ color: "var(--fg-muted)", lineHeight: 1.6 }}>{submitSuccess}</p>
          <button
            onClick={() => setSubmitSuccess(null)}
            style={{
              marginTop: 24, padding: "10px 24px",
              background: "var(--brand-700)", color: "#fff",
              border: "none", borderRadius: 8, fontSize: 14, fontWeight: 600,
              cursor: "pointer", fontFamily: "var(--font-body)",
            }}
          >
            Neue Anfrage stellen
          </button>
        </Card>
      </div>
    );
  }

  return (
    <div style={{ background: "var(--bg)", minHeight: "100dvh", padding: "24px 16px 48px" }}>
      <div style={{ maxWidth: 900, margin: "0 auto" }}>

        {/* Header */}
        <div style={{ marginBottom: 28, textAlign: "center" }}>
          <div style={{ fontSize: 28, fontWeight: 700, color: "var(--brand-900)", fontFamily: "var(--font-display)" }}>
            Buchungsanfrage
          </div>
          <div style={{ fontSize: 14, color: "var(--fg-muted)", marginTop: 6 }}>
            Pfadfinderzentrum Raumünzach · Bitte alle Pflichtfelder ausfüllen.
          </div>
          <div style={{ marginTop: 16 }}>
            <Link
              to="/kalender"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                padding: "8px 16px",
                borderRadius: 8,
                border: "1px solid var(--brand-700)",
                background: "var(--bg-card)",
                color: "var(--brand-700)",
                fontSize: 13,
                fontWeight: 600,
                textDecoration: "none",
                fontFamily: "var(--font-body)",
              }}
            >
              <span className="material-icons-outlined" style={{ fontSize: 16 }}>calendar_month</span>
              Zum Buchungskalender
            </Link>
          </div>
        </div>

        {/* Kalender – vorerst ausgeblendet (wird derzeit nicht benötigt) */}
        {false && (
          <Card style={{ marginBottom: 24 }}>
            <div style={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              marginBottom: 12, flexWrap: "wrap", gap: 8,
            }}>
              <div style={{ fontSize: 15, fontWeight: 700 }}>
                Belegungskalender · {MONTHS_DE[calMonth]} {calYear}
              </div>
              <div style={{ display: "flex", gap: 6 }}>
                <NavBtn onClick={prevMonth} icon="chevron_left" />
                <NavBtn
                  onClick={() => { setCalMonth(today.getMonth()); setCalYear(today.getFullYear()); }}
                  label="Heute"
                />
                <NavBtn onClick={nextMonth} icon="chevron_right" />
              </div>
            </div>
            <div style={{ fontSize: 12, color: "var(--fg-muted)", marginBottom: 10 }}>
              Der Kalender zeigt nur Belegungen an — keine persönlichen Daten.
            </div>
            {calLoading
              ? <div style={{ padding: "20px 0", color: "var(--fg-muted)", fontSize: 13 }}>Lade Kalender…</div>
              : <PublicCalendarView
                  year={calYear}
                  month={calMonth}
                  numDays={calNumDays}
                  entries={calEntries}
                  unitNames={allUnitNames}
                  today={today}
                />
            }
          </Card>
        )}

        {/* Formular */}
        <form onSubmit={handleSubmit} noValidate>

          {/* Kontakt */}
          <SectionCard title="Kontakt">
            <div style={grid2}>
              <Field label="Anrede *">
                <select
                  value={form.anrede}
                  onChange={e => set("anrede")(e.target.value)}
                  style={inputStyle}
                  required
                >
                  <option value="">— bitte wählen —</option>
                  {ANREDEN.map(a => <option key={a}>{a}</option>)}
                </select>
              </Field>
              <Field label="Vorname *">
                <input value={form.vorname} onChange={e => set("vorname")(e.target.value)} style={inputStyle} required />
              </Field>
              <Field label="Nachname *">
                <input value={form.nachname} onChange={e => set("nachname")(e.target.value)} style={inputStyle} required />
              </Field>
              <Field label="E-Mail *">
                <input type="email" value={form.email} onChange={e => set("email")(e.target.value)} style={inputStyle} required />
              </Field>
              <Field label="Telefon (optional)">
                <input type="tel" value={form.telefon} onChange={e => set("telefon")(e.target.value)} style={inputStyle} />
              </Field>
            </div>
          </SectionCard>

          {/* Adresse */}
          <SectionCard title="Adresse">
            <div style={grid2}>
              <Field label="Straße *" fullWidth>
                <input value={form.adresse_strasse} onChange={e => set("adresse_strasse")(e.target.value)} style={inputStyle} required />
              </Field>
              <Field label="PLZ *">
                <input value={form.adresse_plz} onChange={e => set("adresse_plz")(e.target.value)} style={inputStyle} required />
              </Field>
              <Field label="Ort *">
                <input value={form.adresse_ort} onChange={e => set("adresse_ort")(e.target.value)} style={inputStyle} required />
              </Field>
              <Field label="Land">
                <input value={form.adresse_land} onChange={e => set("adresse_land")(e.target.value)} style={inputStyle} />
              </Field>
            </div>
          </SectionCard>

          {/* Gruppe */}
          <SectionCard title="Gruppe">
            <div style={grid2}>
              <Field label="Gruppenname *" fullWidth>
                <input value={form.gruppenname} onChange={e => set("gruppenname")(e.target.value)} style={inputStyle} required />
              </Field>
              <Field label="Gruppenart *" fullWidth>
                <select value={form.gruppenart} onChange={e => set("gruppenart")(e.target.value)} style={inputStyle} required>
                  <option value="">— bitte wählen —</option>
                  {GRUPPENARTEN.map(g => <option key={g}>{g}</option>)}
                </select>
              </Field>
            </div>
          </SectionCard>

          {/* Aufenthalt */}
          <SectionCard title="Aufenthalt">
            <div style={grid2}>
              <Field label="Anreisedatum *">
                <input type="date" value={form.check_in} max={dateRange.fromProps.max} onChange={e => dateRange.setFrom(e.target.value)} style={inputStyle} required />
              </Field>
              <Field label="Anreisezeit">
                <input type="time" value={form.check_in_time} onChange={e => set("check_in_time")(e.target.value)} style={inputStyle} />
              </Field>
              <Field label="Abreisedatum *">
                <input type="date" value={form.check_out} min={dateRange.toProps.min} onChange={e => dateRange.setTo(e.target.value)} style={inputStyle} required />
              </Field>
              <Field label="Abreisezeit">
                <input type="time" value={form.check_out_time} onChange={e => set("check_out_time")(e.target.value)} style={inputStyle} />
              </Field>
            </div>
          </SectionCard>

          {/* Einheiten */}
          <SectionCard title="Gewünschte Einheiten *">
            {!form.gruppenart && (
              <div style={{ fontSize: 13, color: "var(--fg-muted)", marginBottom: 10 }}>
                Bitte erst die Gruppenart auswählen, um die verfügbaren Einheiten zu sehen.
              </div>
            )}
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 8 }}>
              {availableUnits.map(u => {
                const selected = form.selectedUnitIds.includes(u.id);
                return (
                  <button
                    key={u.id}
                    type="button"
                    onClick={() => toggleUnit(u.id)}
                    style={{
                      padding: "7px 16px",
                      borderRadius: 20,
                      border: `2px solid ${selected ? unitColor(u.name) : "var(--border)"}`,
                      background: selected ? unitColor(u.name) : "var(--bg-card)",
                      color: selected ? "#fff" : "var(--fg)",
                      fontSize: 13,
                      fontWeight: 600,
                      cursor: "pointer",
                      fontFamily: "var(--font-body)",
                      transition: "all .12s",
                    }}
                  >
                    {u.name}
                  </button>
                );
              })}
            </div>
            {zeltplatzFehler && (
              <div style={{
                marginTop: 8, padding: "8px 12px", borderRadius: 6,
                background: "color-mix(in oklab, #ef4444 10%, var(--bg))",
                border: "1px solid #ef4444", fontSize: 12, color: "#ef4444",
              }}>
                {zeltplatzFehler}
              </div>
            )}
            {hasConflict && (
              <div style={{
                marginTop: 10, padding: "10px 14px", borderRadius: 6,
                background: "color-mix(in oklab, #ef4444 10%, var(--bg))",
                border: "1px solid #ef4444", fontSize: 12,
              }}>
                <div style={{ fontWeight: 700, color: "#ef4444", marginBottom: 4 }}>
                  ⚠ Belegungskonflikt im gewählten Zeitraum
                </div>
                {conflicts.map((c, i) => (
                  <div key={i} style={{ color: "var(--fg)", marginTop: 2 }}>
                    {c.einheiten_namen}: {formatDe(c.check_in)} – {formatDe(c.check_out)}
                  </div>
                ))}
                <div style={{ marginTop: 6, color: "var(--fg-muted)" }}>
                  Bitte wählen Sie einen anderen Zeitraum oder andere Einheiten.
                </div>
              </div>
            )}
          </SectionCard>

          {/* Notizen */}
          <SectionCard title="Notizen (optional)">
            <textarea
              value={form.nachricht}
              onChange={e => set("nachricht")(e.target.value)}
              maxLength={2000}
              rows={4}
              style={{ ...inputStyle, resize: "vertical" }}
              placeholder="Besondere Wünsche, Anmerkungen…"
            />
            <div style={{ fontSize: 11, color: "var(--fg-muted)", marginTop: 4, textAlign: "right" }}>
              {form.nachricht.length}/2000
            </div>
          </SectionCard>

          {/* Datenschutz + Honeypot + Absenden */}
          <Card style={{ marginBottom: 16 }}>
            {/* Honeypot */}
            <input
              type="text"
              name="website"
              style={{ position: "absolute", left: -9999, opacity: 0, pointerEvents: "none" }}
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
            />

            <label style={{ display: "flex", alignItems: "flex-start", gap: 10, cursor: "pointer", marginBottom: 16 }}>
              <input
                type="checkbox"
                checked={form.datenschutz_ok}
                onChange={e => set("datenschutz_ok")(e.target.checked)}
                style={{ marginTop: 2, flexShrink: 0 }}
              />
              <span style={{ fontSize: 13, lineHeight: 1.5, color: "var(--fg)" }}>
                Ich stimme zu, dass meine Angaben zur Bearbeitung meiner Anfrage gespeichert und verwendet werden.
                Eine Weitergabe an Dritte erfolgt nicht.{" "}
                <span style={{ color: "var(--fg-muted)" }}>*</span>
              </span>
            </label>

            {submitError && (
              <div style={{
                marginBottom: 14, padding: "10px 14px", borderRadius: 6,
                background: "color-mix(in oklab, #ef4444 10%, var(--bg))",
                border: "1px solid #ef4444", fontSize: 13, color: "#ef4444",
              }}>
                {submitError}
              </div>
            )}

            <button
              type="submit"
              disabled={submitting || hasConflict || !!zeltplatzFehler}
              style={{
                width: "100%",
                padding: "12px 0",
                background: submitting || hasConflict || !!zeltplatzFehler ? "var(--fg-muted)" : "var(--brand-700)",
                color: "#fff",
                border: "none",
                borderRadius: 8,
                fontSize: 15,
                fontWeight: 700,
                cursor: submitting || hasConflict || !!zeltplatzFehler ? "not-allowed" : "pointer",
                fontFamily: "var(--font-body)",
                transition: "background .12s",
              }}
            >
              {submitting ? "Wird gesendet…" : "Anfrage absenden"}
            </button>
          </Card>
        </form>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Kleine Hilfskomponenten
// ---------------------------------------------------------------------------

function SectionCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Card style={{ marginBottom: 16 }}>
      <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 14, color: "var(--brand-900)" }}>{title}</div>
      {children}
    </Card>
  );
}

function Field({
  label,
  children,
  fullWidth,
}: {
  label: string;
  children: React.ReactNode;
  fullWidth?: boolean;
}) {
  return (
    <div style={fullWidth ? { gridColumn: "1 / -1" } : {}}>
      <label style={labelStyle}>{label}</label>
      {children}
    </div>
  );
}

function NavBtn({
  onClick, icon, label,
}: {
  onClick: () => void;
  icon?: string;
  label?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        padding: "5px 10px",
        background: "var(--bg-card)",
        border: "1px solid var(--border)",
        borderRadius: 6,
        fontSize: 12,
        fontWeight: 600,
        cursor: "pointer",
        color: "var(--fg)",
        fontFamily: "var(--font-body)",
        display: "flex",
        alignItems: "center",
      }}
    >
      {icon && <span className="material-icons-outlined" style={{ fontSize: 16 }}>{icon}</span>}
      {label}
    </button>
  );
}

const grid2: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(2, 1fr)",
  gap: "12px 16px",
};
